// client/src/config/api.js

// =======================
// DÉTECTION ENV + BASE URL
// =======================
const isBrowser = typeof window !== 'undefined';
const isLocalApp =
  isBrowser &&
  (/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname));

// ⚠️ CORRECTION : Ne pas inclure /api dans la base URL
let baseFromEnv = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// Si l'app tourne en local mais que la base pointe vers un domaine prod,
// on force la base locale pour éviter le CORS.
// DÉSACTIVÉ pour permettre le build de production
// if (isLocalApp && baseFromEnv && !/localhost|127\.0\.0\.1/.test(baseFromEnv)) {
//   console.warn('[API] App en local mais API de prod détectée → forçage API locale');
//   baseFromEnv = 'http://localhost:3002'; // ← Sans /api
// }

// ✅ CORRECTION : Base URL sans /api à la fin
export const API_BASE_URL = baseFromEnv || (isLocalApp ? 'http://localhost:3002' : 'https://florent-escots.fr');
export const API_ORIGIN   = API_BASE_URL;

// =======================
// OUTILS URL
// =======================
const toQS = (query) => {
  if (!query) return '';
  const usp = new URLSearchParams();
  Object.entries(query).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (Array.isArray(v)) v.forEach(val => usp.append(k, String(val)));
    else usp.set(k, String(v));
  });
  const s = usp.toString();
  return s ? `?${s}` : '';
};

// Cache-buster en DEV pour éviter les 304 et confusions de cache
const buildUrl = (path, query) => {
  const q = { ...(query || {}) };
  if (process.env.NODE_ENV === 'development') q.__ts = Date.now();

  // ✅ S'assurer que path commence par /
  let cleanPath = path.startsWith('/') ? path : `/${path}`;

  // ✅ Ajouter /api si pas déjà présent
  if (!cleanPath.startsWith('/api/')) {
    cleanPath = `/api${cleanPath}`;
  }

  return `${API_BASE_URL}${cleanPath}${toQS(q)}`;
};

// =======================
// CACHE MÉMOIRE (client)
// =======================
const __memoryCache = new Map();

// =======================
// APPELS SIMPLES
// =======================
export async function apiGet(path, { query, rawResponse } = {}) {
  const url = buildUrl(path, query);
  console.log('📡 GET:', url); // ← Debug
  
  const res = await fetch(url, {
    credentials: 'include',
    cache: 'no-store',
    headers: { 'Cache-Control': 'no-store' }
  });

  if (rawResponse) return res;

  // Tolérance 304 : renvoie la version en mémoire si dispo
  if (res.status === 304) {
    if (__memoryCache.has(url)) return __memoryCache.get(url);
    throw new Error('HTTP 304 (pas de cache local disponible)');
  }

  if (!res.ok) {
    const errorText = await res.text().catch(() => res.statusText);
    throw new Error(`HTTP ${res.status}: ${errorText}`);
  }

  const json = await res.json();
  __memoryCache.set(url, json);
  return json;
}

export const apiPatch = async (endpoint, data = {}, customHeaders = {}) => {
  // Ajouter /api si nécessaire
  let cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  if (!cleanEndpoint.startsWith('/api/')) {
    cleanEndpoint = `/api${cleanEndpoint}`;
  }

  const url = `${API_BASE_URL}${cleanEndpoint}`;
  const headers = {
    "Content-Type": "application/json",
    ...customHeaders,
  };

  const response = await fetch(url, {
    method: "PATCH",
    headers,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export async function apiPost(path, body, { query, rawResponse, headers } = {}) {
  const url = buildUrl(path, query);
  console.log('📡 POST:', url); // ← Debug
  
  const isFormData = body instanceof FormData;

  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: isFormData ? (headers || {}) : { 'Content-Type': 'application/json', ...(headers || {}) },
    body: isFormData ? body : JSON.stringify(body ?? {})
  });

  if (rawResponse) return res;
  
  if (!res.ok) {
    const errorText = await res.text().catch(() => res.statusText);
    throw new Error(`HTTP ${res.status}: ${errorText}`);
  }
  
  return res.json();
}

export async function apiDelete(path, body, { query, rawResponse, headers } = {}) {
  const url = buildUrl(path, query);
  console.log('📡 DELETE:', url); // ← Debug
  
  const isFormData = body instanceof FormData;

  const res = await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
    cache: 'no-store',
    headers: isFormData ? (headers || {}) : { 'Content-Type': 'application/json', ...(headers || {}) },
    body: body ? (isFormData ? body : JSON.stringify(body)) : undefined
  });

  if (rawResponse) return res;
  
  if (!res.ok) {
    const errorText = await res.text().catch(() => res.statusText);
    throw new Error(`HTTP ${res.status}: ${errorText}`);
  }
  
  return res.json();
}

// =======================
// COMPATIBILITÉ ANCIENS IMPORTS
// =======================
export async function apiCall(pathOrConfig, maybeOptions) {
  const cfg = typeof pathOrConfig === 'string'
    ? { path: pathOrConfig, ...(maybeOptions || {}) }
    : (pathOrConfig || {});

  const { path = '', method = 'GET', body, query, headers, rawResponse } = cfg;
  const m = (method || 'GET').toUpperCase();

  if (m === 'GET')    return apiGet(path,            { query, rawResponse });
  if (m === 'POST')   return apiPost(path, body,     { query, rawResponse, headers });
  if (m === 'DELETE') return apiDelete(path, body,   { query, rawResponse, headers });

  // Fallback générique (PUT, PATCH, etc.)
  const url = buildUrl(path, query);
  console.log(`📡 ${m}:`, url); // ← Debug
  
  const isFormData = body instanceof FormData;

  const res = await fetch(url, {
    method: m,
    credentials: 'include',
    cache: 'no-store',
    headers: isFormData ? (headers || {}) : { 'Content-Type': 'application/json', ...(headers || {}) },
    body: body ? (isFormData ? body : JSON.stringify(body)) : undefined
  });

  if (rawResponse) return res;
  
  if (!res.ok) {
    const errorText = await res.text().catch(() => res.statusText);
    throw new Error(`HTTP ${res.status}: ${errorText}`);
  }
  
  return res.json();
}

// =======================
// PRELOAD CRITIQUE
// =======================
export async function preloadCriticalData() {
  const [health, projects] = await Promise.all([
    apiGet('/health').catch(() => null), // ← Pas de /api
    apiGet('/projects').catch(() => ({ data: {} })) // ← Pas de /api
  ]);
  return { health, projects };
}

// =======================
// AIDE URL D'IMAGE
// =======================
export const getOptimizedImageUrl = (imageUrl) => {
  if (!imageUrl) return '';
  
  // Si c'est déjà une URL complète
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://') || imageUrl.startsWith('data:')) {
    return imageUrl;
  }
  
  // Si c'est un chemin /uploads/, construire l'URL complète SANS /api
  if (imageUrl.startsWith('/uploads/')) {
    // En production
    if (typeof window !== 'undefined' && 
        window.location.hostname !== 'localhost' && 
        window.location.hostname !== '127.0.0.1') {
      return `https://florent-escots.fr${imageUrl}`;
    }
    // En développement
    return `http://localhost:3002${imageUrl}`;
  }
  
  // Pour les autres cas, utiliser API_ORIGIN
  return `${API_ORIGIN}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
};

// =======================
// UPLOAD D'IMAGE
// =======================
export async function uploadImage(file, folder = 'projects') {
  try {
    // Validation
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      throw new Error('Image trop volumineuse (max 50MB)');
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Format non supporté (JPEG, PNG, WebP uniquement)');
    }

    const formData = new FormData();
    formData.append('image', file);
    formData.append('folder', folder);

    // Utiliser apiPost avec FormData
    const result = await apiPost('/api/upload/image', formData);
    
    return result;
  } catch (error) {
    console.error('Erreur upload:', error);
    throw error;
  }
}


export async function uploadHeroImage(file, projectSlug, imageName) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("projectSlug", projectSlug);
  formData.append("imageName", imageName);

  const response = await fetch("/api/upload/image", {
    method: "POST",
    body: formData,
  });

  return await response.json();
}
